/**
 * guideTone — 목표 멜로디를 Web Audio로 들려주는 가이드 톤 재생기 (#13).
 *
 * 절대음감이 아니면 목표 음을 귀로 들어야 따라 부를 수 있다. 멜로디 노트를
 * `startMs/durMs` 타이밍대로 `OscillatorNode`에 스케줄해 재생한다. 연습중에는
 * 곡 0ms(연습중 진입)부터 스케줄하므로 플레이헤드와 같은 곡 타임라인에서 흐른다.
 *
 * 클릭음 방지를 위해 노트마다 부드러운 attack/release 엔벨로프(gain ramp)를 건다.
 * 볼륨은 낮게(가이드용). 브라우저/웹뷰 Web Audio 가정 — dev 전용 패널에서만 쓴다.
 * IO(오디오) 부수효과 모듈이라 순수 헬퍼(midiToFrequency)만 테스트 대상.
 */
import type { TargetMelody } from '@muzly/shared';
import { midiToFreq } from '@muzly/voice-engine';

/** MIDI 음번호 → 주파수(Hz). 환산은 voice-engine이 소유한다. */
export const midiToFrequency = midiToFreq;

/** 가이드용 낮은 피크 볼륨. */
const PEAK_GAIN = 0.08;
/** 클릭음 방지 엔벨로프(초). */
const ATTACK_S = 0.012;
const RELEASE_S = 0.04;
/** 스케줄 시작까지의 여유(초) — 첫 노트가 잘리지 않게. */
const LEAD_S = 0.06;

export interface GuideTonePlayer {
  /** 멜로디를 처음부터 타이밍대로 재생(진행 중이던 재생은 멈추고 새로 시작). */
  play: (melody: TargetMelody) => void;
  /** 스케줄된 모든 톤 정지. */
  stop: () => void;
  /** AudioContext까지 해제(언마운트 시). */
  dispose: () => void;
}

type AudioContextCtor = typeof AudioContext;

const resolveAudioContextCtor = (): AudioContextCtor | undefined =>
  window.AudioContext ??
  // Safari 구형 프리픽스 폴백.
  (window as unknown as { webkitAudioContext?: AudioContextCtor })
    .webkitAudioContext;

/**
 * 가이드 톤 재생기 생성. AudioContext는 첫 재생(사용자 제스처 이후) 때 지연 생성한다.
 */
export const createGuideTonePlayer = (): GuideTonePlayer => {
  let ctx: AudioContext | null = null;
  let active: OscillatorNode[] = [];

  const ensureCtx = (): AudioContext | null => {
    const Ctor = resolveAudioContextCtor();
    if (!Ctor) {
      return null;
    }
    if (!ctx) {
      ctx = new Ctor();
    }
    return ctx;
  };

  const stop = (): void => {
    active.forEach((osc) => {
      try {
        osc.stop();
      } catch {
        // 이미 정지/종료된 노드 — 무시.
      }
      osc.disconnect();
    });
    active = [];
  };

  const scheduleNote = (
    audio: AudioContext,
    base: number,
    midi: number,
    startMs: number,
    durMs: number,
  ): void => {
    const start = base + startMs / 1000;
    const dur = durMs / 1000;
    const osc = audio.createOscillator();
    const gain = audio.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(midiToFrequency(midi), start);
    const g = gain.gain;
    const releaseStart = Math.max(start + ATTACK_S, start + dur - RELEASE_S);
    g.setValueAtTime(0, start);
    g.linearRampToValueAtTime(PEAK_GAIN, start + ATTACK_S);
    g.setValueAtTime(PEAK_GAIN, releaseStart);
    g.linearRampToValueAtTime(0, start + dur);
    osc.connect(gain).connect(audio.destination);
    osc.start(start);
    osc.stop(start + dur + 0.02);
    osc.onended = () => gain.disconnect();
    active.push(osc);
  };

  const play = (melody: TargetMelody): void => {
    const audio = ensureCtx();
    if (!audio) {
      return;
    }
    if (audio.state === 'suspended') {
      void audio.resume();
    }
    stop();
    const base = audio.currentTime + LEAD_S;
    melody.notes.forEach((n) =>
      scheduleNote(audio, base, n.midi, n.startMs, n.durMs),
    );
  };

  const dispose = (): void => {
    stop();
    if (ctx) {
      void ctx.close();
      ctx = null;
    }
  };

  return { play, stop, dispose };
};
